import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, FolderOpen, CalendarClock, FileText,
  Scale, CalendarX2, ReceiptText, Bell, LogOut, Menu, X, UserCog, CalendarDays, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandLockup } from "@/components/LogoMark";
import { api, ROLE_LABELS } from "@/lib/api";
import { ExitBriefPrompt } from "@/components/ExitBriefPrompt";

const navFor = (role) =>
  role === "owner"
    ? [
        { to: "/admin", label: "Overview", icon: LayoutDashboard, id: "admin-overview" },
        { to: "/registry", label: "Fee Ledger", icon: ReceiptText, id: "registry" },
        { to: "/admin/arbitrators", label: "Arbitrator Panel", icon: Scale, id: "admin-arbitrators" },
        { to: "/admin/compliance", label: "Compliance · DPDP", icon: ShieldCheck, id: "admin-compliance" },
        { to: "/profile", label: "Profile & Security", icon: UserCog, id: "profile" },
      ]
    : [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/cases", label: "My Cases", icon: FolderOpen, id: "cases" },
  { to: "/proceedings", label: "Proceedings", icon: CalendarClock, id: "proceedings" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, id: "calendar" },
  { to: "/cause-list", label: "Cause List", icon: FileText, id: "cause-list", roles: ["arbitrator", "advocate", "associate"] },
  { to: "/documents", label: "Documents", icon: FileText, id: "documents" },
  { to: "/orders", label: "Orders & Awards", icon: Scale, id: "orders" },
  { to: "/adjournments", label: "Adjournments", icon: CalendarX2, id: "adjournments" },
  { to: "/fees", label: role === "client" ? "Pay Fees" : "Fees", icon: ReceiptText, id: "fees" },
  { to: "/alerts", label: "Alerts", icon: Bell, id: "alerts" },
  { to: "/profile", label: "Profile & Security", icon: UserCog, id: "profile" },
      ];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/notifications")
      .then(({ data }) => setUnread(data.filter((n) => !n.read).length))
      .catch(() => {});
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const nav = (
    <nav className="flex flex-col gap-1 px-3">
      {navFor(user?.role).filter((n) => !n.roles || n.roles.includes(user?.role)).map(({ to, label, icon: Icon, id }) => (
        <NavLink
          key={to}
          to={to}
          data-testid={`nav-${id}`}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors duration-200 border-l-2 ${
              isActive
                ? "border-l-blue-800 bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white font-medium"
                : "border-l-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-900"
            }`
          }
        >
          <Icon className="h-4 w-4 shrink-0" />
          <span className="flex-1">{label}</span>
          {id === "alerts" && unread > 0 && (
            <span data-testid="unread-badge" className="font-mono text-[10px] bg-red-800 text-white px-1.5">
              {unread}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <ExitBriefPrompt />
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card sticky top-0 h-screen">
        <div className="px-6 py-7 border-b border-border">
          <BrandLockup />
          <p className="label-caps mt-2">Ad-hoc Arbitration</p>
        </div>
        <div className="py-6 flex-1 overflow-y-auto">{nav}</div>
        <div className="border-t border-border px-6 py-5">
          <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
          <p className="label-caps mt-1" data-testid="sidebar-user-role">{ROLE_LABELS[user?.role] || user?.role}</p>
          <button
            data-testid="logout-button"
            onClick={doLogout}
            className="mt-4 flex items-center gap-2 text-xs uppercase tracking-widest text-red-800 dark:text-red-400 hover:text-red-600 transition-colors duration-200"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-50 flex items-center justify-between border-b border-border bg-card px-4 py-3">
          <BrandLockup size="text-lg" mark="h-6 w-6" testId="brand-lockup-mobile" />
          <button data-testid="mobile-menu-toggle" aria-label="Toggle navigation menu" onClick={() => setOpen(!open)}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </header>
        {open && (
          <div className="lg:hidden border-b border-border bg-card py-4" data-testid="mobile-nav">
            {nav}
            <button
              data-testid="logout-button-mobile"
              onClick={doLogout}
              className="mx-6 mt-4 flex items-center gap-2 text-xs uppercase tracking-widest text-red-800"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        )}
        <main className="px-5 py-8 sm:px-8 lg:px-12 lg:py-12 max-w-[1600px]">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
